import React from 'react';
import type { Account, Campaign } from '../../store/appStore';
import { DEMOKRATIUKA_COLORS, getCampaignTheme } from '../../lib/demokratiukaBrand';

interface Props {
  account: Account;
  campaign: Campaign;
  onFollow: () => void;
  onSkipInstagram?: () => void;
}

/* "Jeg har ikke Instagram"-knapp — vises under hovedknappen så folk
   uten Instagram fortsatt kan sende inn. Sekundærstil (hvit + svart kant). */
const SkipInstagramLink: React.FC<{ onSkip?: () => void }> = ({ onSkip }) => {
  if (!onSkip) return null;
  return (
    <button
      type="button"
      onClick={onSkip}
      className="block w-full mt-3 transition-colors"
      style={{
        backgroundColor: '#ffffff',
        color: '#000000',
        fontSize: '1rem',
        fontWeight: 700,
        padding: '0.875rem 1.5rem',
        border: '2px solid #000000',
        cursor: 'pointer',
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.backgroundColor = '#000000';
        e.currentTarget.style.color = '#ffffff';
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.backgroundColor = '#ffffff';
        e.currentTarget.style.color = '#000000';
      }}
    >
      Jeg har ikke Instagram — send inn likevel
    </button>
  );
};

/* ═══════════════════════════════════════════
   "Falske nyheter" — original elaborate design
   ═══════════════════════════════════════════ */
const FalskeNyheterGate: React.FC<Props> = ({ account, campaign, onFollow, onSkipInstagram }) => {
  const cleanHandle = account.instagramUrl.replace('@', '').trim();

  return (
    <div>
      {/* Event image — full width */}
      <img
        src="/demokratiuka/falske-nyheter.jpg"
        alt="Falske nyheter og ekte konsekvenser"
        style={{ width: '100%', display: 'block' }}
      />

      {/* Event info */}
      <div className="mt-8 mb-6">
        <p
          className="uppercase mb-3"
          style={{ color: '#666666', fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.1em' }}
        >
          Samtale og debatt
        </p>
        <h1
          style={{ color: '#000000', fontSize: 'clamp(1.5rem, 5vw, 2.5rem)', fontWeight: 700, lineHeight: 1.2, marginBottom: '1rem' }}
        >
          {campaign.title}
        </h1>

        {/* Date and venue */}
        <div className="mb-6 py-4" style={{ borderTop: '1px solid rgba(0,0,0,0.1)', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
          <p style={{ color: '#000000', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.25rem' }}>
            Lørdag 2. mai 2026 · 19:00 - 20:00
          </p>
          <p style={{ color: '#666666', fontSize: '0.875rem', fontWeight: 400 }}>
            Teateret, hovedscenen · Kristiansand
          </p>
        </div>

        {/* Description — humanized */}
        <div style={{ color: '#222222', fontSize: '1rem', fontWeight: 400, lineHeight: 1.7 }}>
          <p className="mb-4">
            Kristiansanderen Dan Korneli lagde dokumentaren «Mørke skyer» om falske nyheter som preget
            lokalvalget i 2019. Det han fant burde bekymre oss alle.
          </p>
          <p className="mb-4">
            Hvor skadelig er egentlig falske nyheter for demokratiet? Og kan vi stole på de etablerte
            mediene, når noen av dem beundrer politikere som bygger karrieren sin på udokumenterte påstander?
          </p>
          <p className="mb-4">
            Under Demokratiuka samler vi et panel som tar denne samtalen på alvor.
            Har du noe du vil si til dem? Følg oss på Instagram, så kan du sende ditt spørsmål
            eller din kommentar rett til panelet.
          </p>
        </div>

        {/* Panelists */}
        <div className="mb-8 p-4 sm:p-6" style={{ backgroundColor: '#ffffff', border: '2px solid #000000' }}>
          <p
            className="uppercase mb-3"
            style={{ color: '#000000', fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.1em' }}
          >
            Panelet
          </p>
          <p style={{ color: '#000000', fontSize: '1rem', fontWeight: 400, lineHeight: 1.8 }}>
            <span style={{ fontWeight: 600 }}>Hilde Sandvik</span> (panelleder)
            <br />
            <span style={{ fontWeight: 600 }}>Thomas Seltzer</span> — artist og programleder
            <br />
            <span style={{ fontWeight: 600 }}>Danby Choi</span> — redaktør, Subjekt
            <br />
            <span style={{ fontWeight: 600 }}>Dan Korneli</span> — regissør, «Mørke skyer»
            <br />
            <span style={{ color: '#666666' }}>Representanter fra VGTV</span>
          </p>
        </div>
      </div>

      {/* Slik fungerer det — tydelig 3-stegs forklaring */}
      <div
        className="mb-5 px-4 py-4"
        style={{ backgroundColor: '#f5f5f5', border: `1px solid ${DEMOKRATIUKA_COLORS.ytringsfrihet}22` }}
      >
        <p
          className="uppercase mb-3"
          style={{ color: '#666666', fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em' }}
        >
          Slik fungerer det
        </p>
        <ol style={{ color: '#222', fontSize: '0.9375rem', lineHeight: 1.6, fontWeight: 400, margin: 0, paddingLeft: '1.25rem' }}>
          <li style={{ marginBottom: '0.5rem' }}>
            Trykk på knappen — Instagram åpnes i en ny fane.
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            Følg <strong style={{ fontWeight: 700 }}>@{cleanHandle}</strong>, og lukk Instagram-fanen.
          </li>
          <li>
            Du er allerede på riktig side — skriv inn meldingen og send.
          </li>
        </ol>
      </div>

      <button
        onClick={onFollow}
        className="w-full text-white transition-colors"
        style={{
          backgroundColor: '#000000',
          fontSize: '0.875rem',
          fontWeight: 600,
          padding: '0.75rem 1.5rem',
        }}
        onMouseOver={e => (e.currentTarget.style.backgroundColor = DEMOKRATIUKA_COLORS.ytringsfrihet)}
        onMouseOut={e => (e.currentTarget.style.backgroundColor = '#000000')}
      >
        Følg @{cleanHandle} for å sende melding til panelet
      </button>
      <p className="mt-3 text-center" style={{ color: '#999', fontSize: '0.75rem', fontWeight: 400 }}>
        Etter du har fulgt: lukk Instagram-fanen og kom rett tilbake hit.
      </p>
      <SkipInstagramLink onSkip={onSkipInstagram} />
    </div>
  );
};

/* ═══════════════════════════════════════════
   Simple debate gate — for new debates
   ═══════════════════════════════════════════ */
const SimpleDebateGate: React.FC<Props> = ({ account, campaign, onFollow, onSkipInstagram }) => {
  const cleanHandle = account.instagramUrl.replace('@', '').trim();
  const theme = getCampaignTheme(campaign.slug);
  const mutedInk = theme.ink === '#ffffff' ? 'rgba(255,255,255,0.72)' : 'rgba(0,0,0,0.65)';

  return (
    <div>
      {/* Title block — themed per kampanje */}
      <div className="px-4 sm:px-6 py-6 sm:py-8" style={{ backgroundColor: theme.bg }}>
        <p
          className="uppercase mb-3"
          style={{ color: mutedInk, fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.1em' }}
        >
          Send inn til debatten
        </p>
        <h1
          className="leading-tight"
          style={{ color: theme.ink, fontSize: 'clamp(1.5rem, 5vw, 2.5rem)', fontWeight: 700, letterSpacing: '-0.01em' }}
        >
          {campaign.title}
        </h1>
        {campaign.description && (
          <p className="mt-3" style={{ color: mutedInk, fontSize: '1rem', lineHeight: 1.7, fontWeight: 300, whiteSpace: 'pre-line' }}>
            {campaign.description}
          </p>
        )}
      </div>

      <div className="px-4 sm:px-6 py-6 sm:py-8">
        <p className="mb-6" style={{ color: '#222', fontSize: '1rem', lineHeight: 1.7, fontWeight: 400 }}>
          Har du et spørsmål eller en kommentar til panelet? Følg oss på Instagram, kom tilbake hit, og send inn meldingen din. Den blir levert direkte til debatten.
        </p>

        {/* Slik fungerer det — tydelig 3-stegs forklaring */}
        <div
          className="mb-5 px-4 py-4"
          style={{ backgroundColor: '#f5f5f5', border: `1px solid ${DEMOKRATIUKA_COLORS.ytringsfrihet}22` }}
        >
          <p
            className="uppercase mb-3"
            style={{ color: '#666', fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em' }}
          >
            Slik fungerer det
          </p>
          <ol style={{ color: '#222', fontSize: '0.9375rem', lineHeight: 1.6, fontWeight: 400, margin: 0, paddingLeft: '1.25rem' }}>
            <li style={{ marginBottom: '0.5rem' }}>
              Trykk på knappen — Instagram åpnes i en ny fane.
            </li>
            <li style={{ marginBottom: '0.5rem' }}>
              Følg <strong style={{ fontWeight: 700 }}>@{cleanHandle}</strong>, og lukk Instagram-fanen.
            </li>
            <li>
              Du er allerede på riktig side — skriv inn meldingen og send.
            </li>
          </ol>
        </div>

        <button
          onClick={onFollow}
          className="w-full text-white transition-colors"
          style={{
            backgroundColor: '#000000',
            fontSize: '1rem',
            fontWeight: 700,
            padding: '1rem 1.5rem',
          }}
          onMouseOver={e => (e.currentTarget.style.backgroundColor = DEMOKRATIUKA_COLORS.ytringsfrihet)}
          onMouseOut={e => (e.currentTarget.style.backgroundColor = '#000000')}
        >
          Følg @{cleanHandle} og send inn til debatten
        </button>
        <p className="mt-3 text-center" style={{ color: '#999', fontSize: '0.75rem', fontWeight: 400 }}>
          Etter du har fulgt: lukk Instagram-fanen og kom rett tilbake hit.
        </p>
        <SkipInstagramLink onSkip={onSkipInstagram} />
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   Main — picks variant by slug
   ═══════════════════════════════════════════ */
export const DebateFollowGate: React.FC<Props> = (props) => {
  if (props.campaign.slug === 'falske-nyheter') {
    return <FalskeNyheterGate {...props} />;
  }
  return <SimpleDebateGate {...props} />;
};
